<script setup lang="ts">
import BreadCrumb, { Page } from "@/components/BreadCrumb.vue";
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledDropZone from "@/components/StyledDropZone.vue";
import StyledInput from "@/components/StyledInput.vue";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { computed, ref } from "vue";
import CoincidencesList from "../../../components/CoincidencesList.vue";
import ProviderFiltersSlideOver from "../../../components/ProviderFiltersSlideOver.vue";
import ChainList from "./components/ChainList.vue";
import useChainForm from "./composables/useChainForm";
import useChains from "./composables/useChains";
import useSearchChains from "./composables/useSearchChains";

const { data, isLoading, page, refetch, debouncedSetQuery, filters } =
  useSearchChains({
    immediate: true,
    queryKey: "list-chains",
  });

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
    page.value = newPage;
    refetch();
  }
};

const {
  form,
  handleEvaluation,
  handleCreate,
  chains,
  clearState,
  actionModal,
  modalAction,
  createChainLoading,
  isCoincidence,
  createChainMediaLoading,
} = useChainForm();

const { deleteChainAsync, restoreChainAsync } = useChains({
  immediate: false,
});

const pages = ref<Page[]>([
  {
    name: "Proveedores de hoteleria",
    to: {
      name: "hotel",
    },
  },
  {
    name: "Cadenas",
    to: {
      name: "hotel.chain",
    },
  },
]);

const handleDeleteChain = async (id: number) => {
  await deleteChainAsync(id);
  refetch();
};

const handleRestoreChain = async (id: number) => {
  await restoreChainAsync(id);
  refetch();
};

// Slide over filters
const isFiltersOpen = ref(false);

const filtersActive = computed(() => {
  let count = 0;
  if (filters.active !== undefined) count++;
  return count > 0 ? `(${count})` : "";
});

const resetFilters = () => {
  filters.orderBy = "name";
  filters.orderDirection = "asc";
  filters.active = undefined;
};
</script>

<template>
  <BreadCrumb :pages="pages" />

  <main class="mx-auto max-w-7xl px-4 py-6">
    <!-- Header -->
    <div class="mb-6">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Cadenas</h1>
          <p class="mt-1 text-sm text-gray-500">
            Gestiona las cadenas hoteleras y sus marcas asociadas
          </p>
        </div>

        <HasPermission
          :name-permission="'create-internal'"
          :entity-type="'App\\Models\\Chain'"
        >
          <StyledButton
            class="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md"
            @click="actionModal"
          >
            <span class="material-symbols-outlined text-xl">add</span>
            Nueva cadena
          </StyledButton>
        </HasPermission>
      </div>

      <!-- Filters button -->
      <div class="mt-4 flex items-center gap-3">
        <button
          @click="isFiltersOpen = true"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow"
        >
          <span class="material-symbols-outlined text-lg">filter_alt</span>
          Filtros {{ filtersActive }}
        </button>
      </div>
    </div>

    <!-- List -->
    <ChainList
      :data="data?.data || []"
      :is-loading="isLoading"
      @action-modal="actionModal"
      @delete="handleDeleteChain"
      @restore="handleRestoreChain"
    >
      <template #pagination>
        <div class="mt-4 flex justify-end" v-if="data?.data?.length">
          <PaginateNav
            :page="page"
            :pagination="data"
            :data="data?.data || []"
            :handle-page="handlePage"
          />
        </div>
      </template>
    </ChainList>
  </main>

  <!-- Filters SlideOver -->
  <ProviderFiltersSlideOver
    :when="isFiltersOpen"
    :filters="filters"
    title="Filtros de cadenas"
    icon="link"
    icon-bg-color="bg-blue-100"
    icon-color="text-blue-600"
    button-color="bg-blue-600 hover:bg-blue-700"
    @close="isFiltersOpen = false"
    @search="debouncedSetQuery"
    @update:order-by="(v) => (filters.orderBy = v)"
    @update:order-direction="(v) => (filters.orderDirection = v)"
    @update:active="(v) => (filters.active = v)"
    @reset="resetFilters"
  />

  <!-- Modal create -->
  <ModalComponent
    :model-value="modalAction"
    @update:model-value="
      () => {
        clearState();
        actionModal();
      }
    "
  >
    <template #modalBody>
      <div class="p-6">
        <h2 class="mb-6 text-xl font-semibold text-gray-900">Crear nueva cadena</h2>

        <form @submit.prevent="handleEvaluation" class="space-y-4">
          <HasPermission
            :name-permission="'create-medias-internal'"
            :entity-type="'App\\Models\\Chain'"
          >
            <StyledDropZone
              v-model="form.logo"
              class="!w-full"
              :accept-types="['image/png', 'image/jpg', 'image/jpeg']"
            />
          </HasPermission>

          <StyledInput
            v-model="form.name"
            label="Nombre de la cadena"
            placeholder="Ingresa el nombre de la cadena"
            :is-required="true"
            :focus="true"
            :disabled="isCoincidence"
          />

          <StyledButton
            v-if="!isCoincidence"
            type="submit"
            class="w-full rounded-xl bg-blue-600 py-3 text-white hover:bg-blue-700"
            :loading="createChainLoading || createChainMediaLoading"
          >
            Crear cadena
          </StyledButton>

          <CoincidencesList
            :data="chains?.data || []"
            :is-coincidence="isCoincidence"
            :loading-creating="createChainLoading || createChainMediaLoading"
            :handle-action="handleCreate"
            :clear-state="clearState"
            :action-modal="actionModal"
          />
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
